/**
 * Label Families — shape clustering + classification proposals
 *
 * A plan sheet is not thousands of independent strings; it is a handful of
 * repeated label FAMILIES (all string numbers, all DCCB headings, the title
 * block…) plus noise. The fleet audit (2026-09-01) showed the top structured
 * family covers 62–92% of unclassified labels at the worst sites.
 *
 * This module clusters entities by shape signature and drafts a
 * classification proposal per family, so classifying a sheet becomes a few
 * confirmations instead of per-label regex authoring.
 */

import type { LayoutTextEntity, LabelSchema } from './types';
import { autoDetectSegments, suggestLevelFromSegments } from './labelSchemaCompiler';
import { extractMetadata, effectiveClassification } from './classify';

// ── Shape signature ───────────────────────────────────────

/**
 * Collapse a label to its shape: digit runs → '#', Latin runs → 'A',
 * Hebrew runs → 'H', whitespace → '_'; separators kept verbatim.
 * "E30.DC04" → "A#.A#",  "17" → "#",  "7.7A" → "#.#A"
 */
export function shapeSignature(text: string): string {
  return text
    .replace(/[0-9]+/g, '#')
    .replace(/[A-Za-z]+/g, 'A')
    .replace(/[֐-׿]+/g, 'H')
    .replace(/\s+/g, '_');
}

// ── Family clustering ─────────────────────────────────────

export interface LabelFamily {
  /** Shape signature shared by every member */
  signature: string;
  /** Total members */
  count: number;
  /** Members currently unclassified (no classification, no override) */
  unclassified: number;
  /** Distribution of current classification levels (incl. 'unclassified') */
  levels: Record<string, number>;
  /** Up to 6 distinct member values */
  samples: string[];
  /** Proposal seed: a member of the most common character length */
  representative: string;
  /** min/max numeric value — pure-number families only */
  numericRange?: { min: number; max: number };
  /** Any member has a leading zero ("01") — bounded regex must allow it */
  hasLeadingZeros?: boolean;
  /** Contains at least one digit run — candidate for classification */
  structured: boolean;
}

const PURE_NUMBER_RE = /^\d+$/;

/**
 * Cluster entities into shape families, sorted by unclassified count
 * (then total count) descending. Entities with a manual override keep it —
 * they still count toward their family's level distribution.
 */
export function clusterFamilies(entities: LayoutTextEntity[]): LabelFamily[] {
  interface Acc {
    signature: string;
    count: number;
    unclassified: number;
    levels: Record<string, number>;
    samples: Set<string>;
    lengthCounts: Map<number, number>;
    firstByLength: Map<number, string>;
    valueFreq: Map<number, number>;
    pureNumber: boolean;
    hasLeadingZeros: boolean;
  }

  const map = new Map<string, Acc>();

  for (const e of entities) {
    const text = e.normalizedText;
    if (!text) continue;
    // Cluster on the metadata-stripped form: "E34.DC03 (20)" belongs to the
    // same family as "E35.DC03" — the classifier strips the parenthetical
    // before matching, so one schema covers both.
    const { cleaned } = extractMetadata(text);
    const core = cleaned || text;
    const sig = shapeSignature(core);

    let acc = map.get(sig);
    if (!acc) {
      acc = {
        signature: sig,
        count: 0,
        unclassified: 0,
        levels: {},
        samples: new Set(),
        lengthCounts: new Map(),
        firstByLength: new Map(),
        valueFreq: new Map(),
        pureNumber: true,
        hasLeadingZeros: false,
      };
      map.set(sig, acc);
    }

    acc.count++;
    const cls = effectiveClassification(e);
    const level = cls?.level ?? 'unclassified';
    acc.levels[level] = (acc.levels[level] || 0) + 1;
    if (level === 'unclassified') acc.unclassified++;

    // Samples show the raw text (parentheticals included) so the user
    // recognizes their labels; representative uses the cleaned core so
    // schema drafts compile against what the classifier matches.
    if (acc.samples.size < 6) acc.samples.add(text);
    acc.lengthCounts.set(core.length, (acc.lengthCounts.get(core.length) || 0) + 1);
    if (!acc.firstByLength.has(core.length)) acc.firstByLength.set(core.length, core);

    if (PURE_NUMBER_RE.test(core)) {
      const n = parseInt(core, 10);
      acc.valueFreq.set(n, (acc.valueFreq.get(n) || 0) + 1);
      if (core.length > 1 && core.startsWith('0')) acc.hasLeadingZeros = true;
    } else {
      acc.pureNumber = false;
    }
  }

  const families: LabelFamily[] = [];
  for (const acc of map.values()) {
    // Representative: a member of the most common length
    let modalLength = 0;
    let modalCount = -1;
    for (const [len, n] of acc.lengthCounts) {
      if (n > modalCount) {
        modalCount = n;
        modalLength = len;
      }
    }
    const representative = acc.firstByLength.get(modalLength) ?? [...acc.samples][0] ?? '';

    families.push({
      signature: acc.signature,
      count: acc.count,
      unclassified: acc.unclassified,
      levels: acc.levels,
      samples: [...acc.samples],
      representative,
      numericRange:
        acc.pureNumber && acc.valueFreq.size > 0
          ? denseNumericRange(acc.valueFreq)
          : undefined,
      hasLeadingZeros: acc.pureNumber && acc.hasLeadingZeros ? true : undefined,
      structured: acc.signature.includes('#'),
    });
  }

  families.sort((a, b) => b.unclassified - a.unclassified || b.count - a.count);
  return families;
}

/**
 * Range of the FREQUENT values only. In-table string numbers repeat many
 * times (1..24 × one per table); stray numbers — sheet ids, dimensions —
 * appear once or twice and must not stretch the proposed range
 * ("Classify as strings 0–1753" was a real bug caught on Zmorot).
 */
function denseNumericRange(valueFreq: Map<number, number>): { min: number; max: number } {
  let maxFreq = 0;
  for (const f of valueFreq.values()) if (f > maxFreq) maxFreq = f;
  const threshold = Math.max(2, Math.ceil(maxFreq * 0.15));

  let min = Infinity;
  let max = -Infinity;
  for (const [value, freq] of valueFreq) {
    if (freq < threshold) continue;
    if (value < min) min = value;
    if (value > max) max = value;
  }
  if (min === Infinity) {
    // No value repeats enough — fall back to the full observed range
    for (const value of valueFreq.keys()) {
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }
  return { min, max };
}

// ── Bounded numeric regex ─────────────────────────────────

/** Group a fragment iff it is a top-level alternation (concat safety). */
function grp(fragment: string): string {
  return fragment.includes('|') ? `(?:${fragment})` : fragment;
}

/** Fixed-length numeric range → regex fragment (lo and hi same digit count). */
function fixedLengthRange(lo: number, hi: number): string {
  const loS = String(lo);
  const hiS = String(hi);
  if (loS.length === 1) {
    return lo === hi ? loS : `[${loS}-${hiS}]`;
  }
  const loHead = loS[0];
  const hiHead = hiS[0];
  const restLen = loS.length - 1;
  const restMin = 0;
  const restMax = Math.pow(10, restLen) - 1;
  const loRest = parseInt(loS.slice(1), 10);
  const hiRest = parseInt(hiS.slice(1), 10);
  const pad = (n: number) => String(n).padStart(restLen, '0');

  if (loHead === hiHead) {
    return `${loHead}${grp(fixedLengthRangePadded(pad(loRest), pad(hiRest)))}`;
  }

  const parts: string[] = [];
  // Head digit of lo, from loRest up to 99…9
  parts.push(`${loHead}${grp(fixedLengthRangePadded(pad(loRest), pad(restMax)))}`);
  // Full middle heads
  const midLo = parseInt(loHead, 10) + 1;
  const midHi = parseInt(hiHead, 10) - 1;
  if (midLo <= midHi) {
    const headPart = midLo === midHi ? `${midLo}` : `[${midLo}-${midHi}]`;
    parts.push(`${headPart}${'[0-9]'.repeat(restLen)}`);
  }
  // Head digit of hi, from 00…0 up to hiRest
  parts.push(`${hiHead}${grp(fixedLengthRangePadded(pad(restMin), pad(hiRest)))}`);
  return parts.join('|');
}

/** Same-length (zero-padded) decimal strings → regex fragment. */
function fixedLengthRangePadded(loS: string, hiS: string): string {
  if (loS.length === 1) {
    return loS === hiS ? loS : `[${loS}-${hiS}]`;
  }
  const loHead = loS[0];
  const hiHead = hiS[0];
  if (loHead === hiHead) {
    return `${loHead}${grp(fixedLengthRangePadded(loS.slice(1), hiS.slice(1)))}`;
  }
  const restLen = loS.length - 1;
  const nines = '9'.repeat(restLen);
  const zeros = '0'.repeat(restLen);
  const parts: string[] = [];
  parts.push(`${loHead}${grp(fixedLengthRangePadded(loS.slice(1), nines))}`);
  const midLo = parseInt(loHead, 10) + 1;
  const midHi = parseInt(hiHead, 10) - 1;
  if (midLo <= midHi) {
    const headPart = midLo === midHi ? `${midLo}` : `[${midLo}-${midHi}]`;
    parts.push(`${headPart}${'[0-9]'.repeat(restLen)}`);
  }
  parts.push(`${hiHead}${grp(fixedLengthRangePadded(zeros, hiS.slice(1)))}`);
  return parts.join('|');
}

/**
 * Regex matching decimal integers min..max inclusive (no leading zeros
 * unless allowLeadingZeros). E.g. (1, 24) → "2[0-4]|1[0-9]|[1-9]"-style.
 * Supports 1..99999; the practical range for string/module numbering.
 */
export function boundedNumericRegex(
  min: number,
  max: number,
  allowLeadingZeros = false,
): string {
  const lo = Math.max(0, Math.floor(min));
  const hi = Math.max(lo, Math.floor(max));

  const parts: string[] = [];
  // Split by digit length, longest first so alternation prefers long matches
  for (let len = String(hi).length; len >= String(lo).length; len--) {
    const lenLo = Math.max(lo, len === 1 ? 0 : Math.pow(10, len - 1));
    const lenHi = Math.min(hi, Math.pow(10, len) - 1);
    if (lenLo > lenHi) continue;
    parts.push(fixedLengthRange(lenLo, lenHi));
  }
  const core = parts.join('|');
  return allowLeadingZeros ? `0*(?:${core})` : core;
}

// ── Proposals ─────────────────────────────────────────────

export type FamilyProposal =
  | {
      kind: 'pattern';
      /** Ready-to-use classification pattern (id assigned at apply time) */
      regex: string;
      level: string;
      captureGroups: Record<string, number>;
      actionLabel: string;
    }
  | {
      kind: 'schema';
      schema: LabelSchema;
      actionLabel: string;
    }
  | {
      kind: 'ignore';
      actionLabel: string;
    };

// Measurements ("5.00m", "46.75kWp", "12DEG") and slash-dates
// ("18/03/2015") — structured shapes that are never topology labels.
// Deliberately narrow: bare A/B suffixes ("7.7A") are half-string ids on
// real sheets and dot-chains ("1.18.12") are real string labels, so
// neither counts as noise here.
const UNIT_SAMPLE_RE = /^-?\d+(?:\.\d+)?\s*(?:mm|cm|km|m|kwp|kva|kw|w|deg|°)$/i;
const SLASH_DATE_RE = /^\d{1,2}\/\d{1,2}\/\d{2,4}$/;

function isNoiseFamily(family: LabelFamily): boolean {
  if (family.samples.length === 0) return false;
  return (
    family.samples.every((s) => UNIT_SAMPLE_RE.test(s)) ||
    family.samples.every((s) => SLASH_DATE_RE.test(s))
  );
}

/**
 * Draft a classification proposal for a family.
 *
 * - Pure-number family → bounded-range custom pattern at string level
 *   (bare in-table string numbers: the #1 unclassified family fleet-wide).
 * - Structured family → schema draft from the representative sample
 *   (user confirms segment → field mapping in the Label Schema panel).
 * - Unstructured (words, Hebrew, symbols) and unit/date noise → ignore.
 */
export function proposeForFamily(family: LabelFamily): FamilyProposal {
  if (!family.structured) {
    return { kind: 'ignore', actionLabel: 'Ignore family' };
  }
  if (isNoiseFamily(family)) {
    return { kind: 'ignore', actionLabel: 'Ignore (units / dates)' };
  }

  if (family.numericRange) {
    const { min, max } = family.numericRange;
    const regex = `^(${boundedNumericRegex(min, max, family.hasLeadingZeros)})$`;
    return {
      kind: 'pattern',
      regex,
      level: 'string',
      captureGroups: { string: 1 },
      actionLabel: `Classify as strings ${min}–${max}`,
    };
  }

  const segments = autoDetectSegments(family.representative);
  const level = suggestLevelFromSegments(segments);
  return {
    kind: 'schema',
    schema: { sampleLabel: family.representative, segments, level },
    actionLabel: 'Draft schema',
  };
}
